import { api, ApiError, NetworkError, RequestTimeoutError, statusMessage } from '$lib/shared/api/client';
import type {
  Campaign,
  CampaignAnswer,
  CampaignResponseDetail,
  ClaraSession,
  ClaraSystemSettings,
} from '$shared/types/api';

/**
 * The reflection someone does before they have a STAT account.
 *
 * Every call here is unauthenticated and carries an opaque reflection token instead of a bearer
 * JWT — a guest has no Cognito identity yet, and deliberately does not need one to reflect.
 */

const GUEST_TOKEN_HEADER = 'X-Stat-Guest-Token';

export type ClaraGuestReflectionStatus = 'Reflecting' | 'Completed' | 'Attached';

export interface GuestClaraReflection {
  session: ClaraSession;
  guestStatus: ClaraGuestReflectionStatus;
  expiresAt: string;
  /** Absent until generation finishes; the page polls until it appears. */
  campaign?: Campaign | null;
  /** Answers already given, so a refresh resumes rather than restarts. */
  answers: CampaignAnswer[];
}

export interface StartedGuestReflection {
  sessionId: string;
  guestToken: string;
  expiresAt: string;
}

/**
 * Turns a partner capture into a reflection nobody owns yet. The capture id is the credential —
 * unguessable, single use, and already vouched for by the partner gate that created it.
 *
 * The token comes back exactly once, because the server keeps only a hash. Persist it before
 * doing anything else or the reflection becomes unreachable.
 */
export async function startGuestClaraReflection(captureId: string): Promise<StartedGuestReflection> {
  return api.post<StartedGuestReflection>(
    `/api/clara/embed/captures/${encodeURIComponent(captureId)}/reflection`,
    {},
    { skipAuth: true, retry: false, timeout: 60_000 }
  );
}

/**
 * Keyed by session id rather than capture id: an emailed claim link resolves to a session and
 * knows nothing about the capture it came from.
 */
const GUEST_SESSION_KEY_PREFIX = 'claraGuestReflection:';

export interface StoredGuestSession {
  sessionId: string;
  guestToken: string;
  expiresAt: string;
}

/**
 * Held in sessionStorage rather than localStorage on purpose. The token is the only thing
 * protecting an unattached reflection, and clinic computers are shared — scoping it to the tab
 * means closing the tab ends the guest's access, which is the right default for someone who has
 * deliberately not identified themselves.
 */
export function rememberGuestSession(value: StoredGuestSession): void {
  try {
    sessionStorage.setItem(GUEST_SESSION_KEY_PREFIX + value.sessionId, JSON.stringify(value));
  } catch {
    /* Private browsing and storage-blocked contexts: the flow degrades, it must not break. */
  }
}

export function readGuestSession(sessionId: string): StoredGuestSession | null {
  try {
    const raw = sessionStorage.getItem(GUEST_SESSION_KEY_PREFIX + sessionId);
    return raw ? (JSON.parse(raw) as StoredGuestSession) : null;
  } catch {
    return null;
  }
}

export function forgetGuestSession(sessionId: string): void {
  try {
    sessionStorage.removeItem(GUEST_SESSION_KEY_PREFIX + sessionId);
  } catch {
    /* As above. */
  }
}

export async function getGuestClaraReflection(sessionId: string, guestToken: string): Promise<GuestClaraReflection> {
  return api.get<GuestClaraReflection>(`/api/clara/embed/reflection/${encodeURIComponent(sessionId)}`, {
    skipAuth: true,
    retry: false,
    headers: { [GUEST_TOKEN_HEADER]: guestToken },
  });
}

/**
 * Shaped to match `submitCampaignResponse` exactly, so `CampaignQuestionFlow` can be handed this
 * instead and behave identically for a guest.
 */
export function guestSubmitResponse(sessionId: string, guestToken: string) {
  return async (
    _campaignId: string,
    payload: { answers: CampaignAnswer[]; isFinalSubmit: boolean; allowFollowupContact?: boolean }
  ): Promise<CampaignResponseDetail> => {
    const path = payload.isFinalSubmit ? 'complete' : 'answers';
    return api.post<CampaignResponseDetail>(
      `/api/clara/embed/reflection/${encodeURIComponent(sessionId)}/${path}`,
      { answers: payload.answers },
      { skipAuth: true, retry: false, timeout: 120_000, headers: { [GUEST_TOKEN_HEADER]: guestToken } }
    );
  };
}

/**
 * A guest cannot read /api/clara/settings, which is authenticated. The flow only uses these
 * values to tune answer-length hints, so defaults are the right fallback rather than a blocker.
 */
export async function getGuestClaraSettings(): Promise<ClaraSystemSettings> {
  return { enabled: true, maxGenerationsPerUserPerDay: 20 };
}

export function guestReflectionHref(sessionId: string): string {
  return `/clara/reflection/${encodeURIComponent(sessionId)}`;
}

/**
 * What to say when the same request may work in a moment (no answer, a timeout or a server fault), so the
 * page can offer Try again. Null for a definite answer (expired, used or refused) that retrying won't change.
 */
export function temporaryFailureMessage(error: unknown): string | null {
  if (error instanceof ApiError) {
    return error.status >= 500 || error.status === 408 || error.status === 429 ? error.message : null;
  }
  if (error instanceof NetworkError) return error.message;
  if (error instanceof RequestTimeoutError) return statusMessage(408);
  return statusMessage(500);
}

/** Sends the emailed claim link for a finished reflection. Content follows only after the click. */
export async function claimGuestClaraReflection(sessionId: string, guestToken: string, email: string): Promise<void> {
  await api.post<void>(
    `/api/clara/embed/reflection/${encodeURIComponent(sessionId)}/claim`,
    { email },
    { skipAuth: true, retry: false, headers: { [GUEST_TOKEN_HEADER]: guestToken } }
  );
}

export interface VerifiedReflectionClaim {
  sessionId: string;
  guestToken: string;
  email: string;
  expiresAt: string;
}

/**
 * Opening the emailed link proves the address. The token travels in the body rather than the URL
 * so it stays out of access logs and Referer headers.
 */
export async function verifyReflectionClaim(token: string): Promise<VerifiedReflectionClaim> {
  return api.post<VerifiedReflectionClaim>(
    '/api/clara/embed/reflection/claims/verify',
    { token },
    { skipAuth: true, retry: false }
  );
}

/**
 * Attaches the reflection in this tab to the account that just signed in. Needs both the JWT and
 * the guest token — being signed in is not on its own a claim to an unattached reflection.
 */
export async function attachGuestReflection(sessionId: string, guestToken: string): Promise<void> {
  await api.post<{ sessionId: string }>(
    `/api/clara/sessions/${encodeURIComponent(sessionId)}/attach-guest`,
    { guestToken },
    { retry: false }
  );
}

/**
 * Picks up anything this person claimed by email before they had an account. Safe to call on
 * every sign-in: it returns zero when there is nothing waiting.
 */
export async function attachClaimedReflections(): Promise<{ attached: number }> {
  return api.post<{ attached: number }>('/api/clara/reflections/attach-claimed', {}, { retry: false });
}
